import {useEffect, useRef, useState} from "react";

function removeStoredCooldown(storageKey) {
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // Persistence is optional; a blocked store must not disable the form.
  }
}

function persistCooldown(storageKey, cooldown) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(cooldown));
  } catch {
    // The in-memory countdown still protects this mounted form.
  }
}

function loadCooldown(storageKey) {
  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey));
    const hasValidEndsAt = typeof stored?.endsAt === "number"
      && Number.isFinite(stored.endsAt)
      && stored.endsAt > Date.now();
    const hasValidMessage = stored?.message === undefined || typeof stored.message === "string";
    if (hasValidEndsAt && hasValidMessage) {
      return stored;
    }
  } catch {
    // A corrupt browser value should never prevent the visitor from requesting a link.
  }
  removeStoredCooldown(storageKey);
  return null;
}

function secondsUntil(endsAt) {
  return Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
}

export default function useResendCooldown({storageKey, initialCooldownSeconds = 0}) {
  const persistedCooldown = useRef();
  if (persistedCooldown.current === undefined) {
    persistedCooldown.current = {value: loadCooldown(storageKey)};
  }
  const persisted = persistedCooldown.current.value;
  const initialEndsAt = persisted?.endsAt
    || (initialCooldownSeconds > 0 ? Date.now() + initialCooldownSeconds * 1000 : 0);
  const [feedback, setFeedback] = useState(persisted?.message || "");
  const [endsAt, setEndsAt] = useState(initialEndsAt);
  const [remainingSeconds, setRemainingSeconds] = useState(secondsUntil(initialEndsAt));

  useEffect(() => {
    if (!endsAt) return undefined;

    const updateCountdown = () => {
      const remaining = secondsUntil(endsAt);
      setRemainingSeconds(remaining);
      if (!remaining) {
        removeStoredCooldown(storageKey);
        window.clearInterval(interval);
      }
    };
    const interval = window.setInterval(updateCountdown, 1000);
    updateCountdown();
    return () => window.clearInterval(interval);
  }, [endsAt, storageKey]);

  const beginCooldown = (seconds, message) => {
    const deadline = Date.now() + seconds * 1000;
    const cooldown = {endsAt: deadline, message};
    persistCooldown(storageKey, cooldown);
    setFeedback(message);
    setEndsAt(deadline);
    setRemainingSeconds(seconds);
  };

  return {remainingSeconds, feedback, setFeedback, beginCooldown};
}
